import { contentAssetUrl, contentCatalog } from "game-common/content/catalog"
import { itemSpriteSheets } from "game-common/item/item_sprite_sheets"
import { Application, BitmapFont, Loader, SCALE_MODES, settings } from "pixi.js"
import { Audio } from "./audio"
import { Client } from "./client"
import { ClientGameLogic } from "./client_game_logic"
import { isMobile, SCALER, ScalingParams } from "./client_util"
import { canvasLayout } from "./canvas_layout"
import { observeCanvasViewport } from "./canvas_viewport"
import { syncUiMetrics } from "./rendering/impl/pixijs/gui/ui_metrics"
import { logger } from "./logger"
import { Keyboard } from "./rendering/impl/pixijs/input/keyboard"
import { Mouse } from "./rendering/impl/pixijs/input/mouse"
import { resolveConfig } from "./runtime_config"
import { PixiJsRenderingFactory } from "./rendering/impl/pixijs/pixijs_rendering_factory"
import { spriteSheetLoader } from "./rendering/impl/pixijs/sprites/spritesheet_loader"

settings.SCALE_MODE = SCALE_MODES.NEAREST
settings.RENDER_OPTIONS.antialias = false

// Every asset url in the client is a logical key ("assets/foo.png") that `contentAssetUrl`
// (game-common/content/catalog) resolves to the current immutable release,
// "assets/content/<revision>/foo.png". That is still relative, so it resolves against whatever
// origin served the page. That is right for a browser tab and wrong for the native shell, whose
// origin is the app bundle -- the art is not shipped inside it. Setting the loader's baseUrl once
// redirects all 50-odd call sites without touching any of them; it is "" everywhere else, and
// resource-loader leaves urls alone when it is empty. Must happen before the first
// Loader.shared.add(), which is why it sits at module top rather than inside the boot callback.
Loader.shared.baseUrl = resolveConfig().assetBase

const foo = setInterval(() => {
    if (window["__GO__"]) {
        clearInterval(foo)
        const app = new Application({
            view: document.getElementById("pixi-canvas") as HTMLCanvasElement,
            resolution: window.devicePixelRatio || 2,
            autoDensity: true,
            backgroundColor: 0x000000,
        })

        Keyboard.initialize()
        Mouse.initialize(app)

        const onLoadComplete = () => {
            const canvasHost = document.getElementById("pixi-content")
            function resize() {
                // Measure the CSS viewport, which can settle after window.innerHeight on iOS
                // Home Screen launch. Never freeze the host to an initial inline pixel height.
                const bounds = canvasHost.getBoundingClientRect()
                const layout = canvasLayout(bounds.width || window.innerWidth, bounds.height || window.innerHeight,
                    window.devicePixelRatio || 1, isMobile())
                const { resolution } = layout
                // Draw at the displayed density, with a buffer budget that includes the wider view.
                app.renderer.resolution = resolution
                // InteractionManager copies the resolution once, when it is constructed, and never
                // looks at it again. Leave it stale and every interactive widget -- and the mouse
                // position the world is aimed with -- hit-tests in the wrong place.
                const interaction = (app.renderer as any).plugins?.interaction
                if (interaction) {
                    interaction.resolution = resolution
                }
                app.renderer.resize(layout.virtualWidth, layout.virtualHeight)

                // resize() with autoDensity has just written the VIRTUAL size into the canvas
                // style; the displayed size is what belongs on screen.
                app.renderer.view.style.width = layout.cssWidth + "px"
                app.renderer.view.style.height = layout.cssHeight + "px"

                app.stage.scale.y = SCALER
                // Existing HUD and camera coordinates stay centered in the expanded canvas.
                app.stage.x = layout.stageX
                app.stage.y = layout.stageY
                ScalingParams.viewportPaddingX = layout.stageX / ScalingParams.surfaceZoom
                ScalingParams.viewportPaddingY = layout.stageY / SCALER / ScalingParams.surfaceZoom
                syncUiMetrics(app.renderer.view, layout.virtualWidth, layout.virtualHeight, SCALER)
            }

            resize()

            const factory: PixiJsRenderingFactory = new PixiJsRenderingFactory(app)
            const clientGameLogic = new ClientGameLogic(factory)
            const client: Client = new Client(clientGameLogic)
            const resizeClient = () => {
                resize()
                clientGameLogic.invalidateViewportCache()
                window.dispatchEvent(new Event("samsara:canvas-resize"))
            }
            observeCanvasViewport(canvasHost, resizeClient)
            window.matchMedia?.("(any-pointer: coarse)").addEventListener?.("change", resizeClient)

            // Setup viewport culling debug tools
            import("./debug_viewport_culling").then(({ setupViewportCullingDebug }) => {
                setupViewportCullingDebug(clientGameLogic.clientRenderer)
            })

            const urlSearchParams = new URLSearchParams(window.location.search)
            const params = Object.fromEntries(urlSearchParams.entries())
            const showsIntro = window["IS_ITCH"] || resolveConfig().autoGuest || (!params.accessToken && params.guest === "true")
            if (!showsIntro) {
                requestAnimationFrame(() => window["stopLoading"]?.())
            }
            if (window["IS_ITCH"] || resolveConfig().autoGuest) {
                client.connect("", "", true)
            } else {
                const { accessToken, guest } = params

                if (accessToken) {
                    client.connect("", accessToken)
                } else {
                    const isGuest = guest === "true"
                    const cachedPlayerName = isGuest ? "" : localStorage.getItem("_playerName_") || ""
                    client.connect(cachedPlayerName, "", isGuest)
                }
            }

            // ONE thrown exception out of a frame must not end the game.
            //
            // pixi's ticker re-arms its requestAnimationFrame AFTER emitting, so a listener that
            // throws stops the loop for good: the canvas holds its last frame, the socket stays up,
            // and the player is left looking at a picture. That is the "my screen froze" report, and
            // it is indistinguishable from a crash however small the thing that threw -- a widget
            // animating a panel, a sprite whose paperdoll was swapped under it, a mask on a destroyed
            // display object. Nothing drawn in a single frame is worth the whole renderer, so: keep
            // drawing, and make sure the first few failures carry a stack somebody can act on.
            //
            // The count AND the stacks go on `window` deliberately. On a device there is no console
            // to scroll back through, and even in a browser the console is no use here: this client
            // logs a couple of thousand texture warnings on load, so by the time a frame fails its
            // stack is somewhere in the middle of that and cannot be found. `__frameErrors` answers
            // "has this client been dropping frames", and `__frameErrorLog` says what it was --
            // readable over CDP, from devtools, or by a test.
            let frameErrors = 0
            const frameErrorLog: string[] = []
            ;(window as any).__frameErrors = 0
            ;(window as any).__frameErrorLog = frameErrorLog
            app.ticker.add(() => {
                try {
                    client.update()
                } catch (error) {
                    frameErrors++
                    ;(window as any).__frameErrors = frameErrors
                    // The first few only: a frame that fails tends to fail every frame, and the
                    // hundredth copy of one stack is worth less than the first copy of the next.
                    if (frameErrors <= 5) {
                        const detail = (error as Error)?.stack || String(error)
                        frameErrorLog.push(detail)
                        console.error("[frame] update failed", error)
                        logger.error({ tags: ["frame", "error"] }, `frame update failed: ${detail}`)
                    }
                }
            })

            // Expose shadow debug features to global scope for debugging
            if (typeof window !== "undefined") {
                // The scene graph, under the name the PixiJS devtools extension looks for. Also
                // the only way to inspect the render tree on a device, where there is no console
                // to poke at -- attach over CDP and walk app.stage from here.
                ;(window as any).__PIXI_APP__ = app
                // Expose logger to global scope for debugging
                ;(window as any).logger = logger
                ;(window as any).shadowDebug = {
                    getMetrics: () => client.getShadowPerformanceMetrics(),
                    forceUpdate: () => client.forceShadowTextureUpdate(),
                    logMetrics: () => {
                        const metrics = client.getShadowPerformanceMetrics()
                        logger.info({ tags: ["shadow", "performance"] }, "Shadow Performance:", metrics)
                    },
                    debugLog: () => {
                        // Access the shadow rendering directly for debug logging
                        const renderer = client.clientGameLogic.clientRenderer as any
                        if (renderer.shadowRendering) {
                            renderer.shadowRendering.debugLog()
                        }
                    },
                    enableDebugLogging: () => {
                        // Enable debug logging for hash calculation
                        const renderer = client.clientGameLogic.clientRenderer as any
                        if (renderer.shadowRendering) {
                            renderer.shadowRendering.debugHashCalculation = true
                            logger.info({ tags: ["shadow", "debug"] }, "Shadow hash debug logging enabled")
                        }
                    },
                    disableDebugLogging: () => {
                        // Disable debug logging
                        const renderer = client.clientGameLogic.clientRenderer as any
                        if (renderer.shadowRendering) {
                            renderer.shadowRendering.debugHashCalculation = false
                            logger.info({ tags: ["shadow", "debug"] }, "Shadow hash debug logging disabled")
                        }
                    },
                }
                logger.info({ tags: ["shadow", "debug"] }, "Shadow debug tools available at window.shadowDebug")
                logger.info({ tags: ["shadow", "debug"] }, "Shadow debug test available at window.shadowDebugTest")
                logger.info({ tags: ["logger", "debug"] }, "Logger available at window.logger for debugging")

                // Add debug test
                ;(window as any).shadowDebugTest = {
                    startMonitoring: () => {
                        const { ShadowDebugTest } = require("./rendering/impl/pixijs/shadow_debug_test")
                        const debugTest = new ShadowDebugTest(clientGameLogic)
                        debugTest.startMonitoring()
                    },
                    testStationary: () => {
                        const { ShadowDebugTest } = require("./rendering/impl/pixijs/shadow_debug_test")
                        const debugTest = new ShadowDebugTest(clientGameLogic)
                        debugTest.testStationary()
                    },
                    testMovement: () => {
                        const { ShadowDebugTest } = require("./rendering/impl/pixijs/shadow_debug_test")
                        const debugTest = new ShadowDebugTest(clientGameLogic)
                        debugTest.testMovement()
                    },
                }
            }
        }

        loadAssets()

        const aggressiveLoad = false
        Loader.shared.load(
            aggressiveLoad
                ? () =>
                      spriteSheetLoader.bulkLoad(
                          contentCatalog.source.preload.map(sheetId => `assets/${sheetId}`),
                          onLoadComplete,
                      )
                : onLoadComplete,
        )
    }
}, 1000)

const loadAssets = () => {
    //
    // fonts
    //
    const chars = {
        chars: [["a", "z"], ["0", "9"], ["A", "Z"], " .!?|,\":<>~!@#$%^&*()_+-=/\\'ë"],
    }

    BitmapFont.from(
        "LocationFont",
        {
            fill: "#00FF00",
            fontSize: 25,
        },
        chars,
    )

    BitmapFont.from(
        "BannerFont",
        {
            fill: "#FFFFFF",
            fontSize: 20,
        },
        chars,
    )

    BitmapFont.from(
        "ImportantFont",
        {
            fill: "#FFFFFF",
            fontSize: 30,
        },
        chars,
    )

    BitmapFont.from(
        "LogoFont",
        {
            fill: "#FFFFFF",
            fontSize: 40,
        },
        chars,
    )

    BitmapFont.from(
        "ScoreFont",
        {
            fill: "#FFFFFF",
            fontSize: 15,
            fontFamily: "Serif",
        },
        chars,
    )

    BitmapFont.from(
        "NameFont",
        {
            fill: "#FFFFFF",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "ChatFont",
        {
            fill: "#00EE00",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "RedChatFont",
        {
            fill: "#FFA500",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "SmallFont",
        {
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "SignFont",
        {
            fill: "#000000",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "ChatBubbleFont",
        {
            fill: "#FFFFFF",
            fontSize: 15,
        },
        chars,
    )

    BitmapFont.from(
        "BlinkFont",
        {
            fill: "#00FF00",
            fontSize: 10,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "ChatHistoryFont",
        {
            fill: "#00FF00",
            fontSize: 10,
        },
        chars,
    )

    BitmapFont.from(
        "LeaderboardFont",
        {
            fill: "#00FF00",
            fontSize: 15,
        },
        chars,
    )

    BitmapFont.from(
        "LeaderboardLeaderFont",
        {
            fill: "#FFFFFF",
            fontSize: 15,
        },
        chars,
    )

    BitmapFont.from(
        "HpFont",
        {
            fill: "#FFFFFF",
            fontSize: 12,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "IdFont",
        {
            fill: "#00FF00",
            fontSize: 12,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "Normal-Green-Bold",
        {
            fill: "#00FF00",
            fontSize: 15,
            fontWeight: "bold",
        },
        chars,
    )

    BitmapFont.from(
        "TinyFont",
        {
            fill: "#FFFFFF",
            fontSize: 9,
        },
        chars,
    )

    //
    // audio
    //
    Audio.loadAssets()

    Loader.shared
        .add("hit1", contentAssetUrl("assets/strike1.ogg"))
        .add("hit2", contentAssetUrl("assets/strike2.ogg"))
        .add("woodhit", contentAssetUrl("assets/woodhit.ogg"))
        .add("bowshot", contentAssetUrl("assets/shoot.ogg"))
        .add("swordswing", contentAssetUrl("assets/swordswing.ogg"))
        .add("explosion", contentAssetUrl("assets/explosion.mp3"))
        .add("emp", contentAssetUrl("assets/emp.mp3"))
        .add("health", contentAssetUrl("assets/health.mp3"))
        .add("mana", contentAssetUrl("assets/mana.mp3"))
        .add("door_open", contentAssetUrl("assets/door_open.ogg"))
        .add("door_close", contentAssetUrl("assets/door_close.ogg"))
        .add("clocktick", contentAssetUrl("assets/clocktick.mp3"))
        .add("powerup", contentAssetUrl("assets/powerup.mp3"))
        .add("map_location", contentAssetUrl("assets/map_location.ogg"))
        .add("click", contentAssetUrl("assets/click.mp3"))
        .add("coins", contentAssetUrl("assets/coin3.ogg"))
        .add("cloth", contentAssetUrl("assets/cloth.ogg"))
        // procedurally synthesized (packages/tools/src/sfx/gen.py)
        .add("campfire_lit", contentAssetUrl("assets/campfire_lit.ogg"))
        .add("campfire_fed", contentAssetUrl("assets/campfire_fed.ogg"))
        .add("wheel_select", contentAssetUrl("assets/wheel_select.ogg"))
        .add("item_crafted", contentAssetUrl("assets/item_crafted.ogg"))
        .add("wave_starting", contentAssetUrl("assets/wave_starting.ogg"))
        .add("visitor_arrives", contentAssetUrl("assets/visitor_arrives.ogg"))
        .add("chapter_task_complete", contentAssetUrl("assets/chapter_task_complete.ogg"))
        .add("chapter_task_stamp", contentAssetUrl("assets/chapter_task_stamp.ogg"))
        .add("chapter_complete", contentAssetUrl("assets/chapter_complete.ogg"))
        .add("chapter_announce", contentAssetUrl("assets/chapter_announce.ogg"))
        .add("chapter_begin", contentAssetUrl("assets/chapter_begin.ogg"))
        .add("bonecaller_chant", contentAssetUrl("assets/bonecaller_chant.ogg"))
        .add("bonecaller_complete", contentAssetUrl("assets/bonecaller_complete.ogg"))
        .add("bonecaller_spike_windup", contentAssetUrl("assets/bonecaller_spike_windup.ogg"))
        .add("bonecaller_spike_impact", contentAssetUrl("assets/bonecaller_spike_impact.ogg"))
        .add("bonecaller_sweep_windup", contentAssetUrl("assets/bonecaller_sweep_windup.ogg"))
        .add("bonecaller_sweep_impact", contentAssetUrl("assets/bonecaller_sweep_impact.ogg"))
        .add("ember_core_charge", contentAssetUrl("assets/ember_core_charge.ogg"))
        .add("ember_core_ready", contentAssetUrl("assets/ember_core_ready.ogg"))
        .add("ember_core_fire", contentAssetUrl("assets/ember_core_fire.ogg"))
        .add("ember_core_fuse", contentAssetUrl("assets/ember_core_fuse.ogg"))
        .add("ember_core_blast", contentAssetUrl("assets/ember_core_blast.ogg"))
        .add("ground_rupture", contentAssetUrl("assets/ground_rupture.ogg"))

    //
    // image
    //
    itemSpriteSheets.forEach(sheet => Loader.shared.add(sheet.id, contentAssetUrl(`assets/${sheet.file}`)))
    Loader.shared
        .add("outer", contentAssetUrl("assets/joystick.png"))
        .add("inner", contentAssetUrl("assets/joystick-handle.png"))
        .add("reddit", contentAssetUrl("assets/reddit.png"))
        .add("discord", contentAssetUrl("assets/discord.png"))
        .add("intro_scene", contentAssetUrl("assets/intro/scene.png"))
}
